import { useMemo, useState } from 'react'

const RATIOS = [
  { id: 'golden', label: 'Golden Ratio', value: 1.618, tip: '1.618 : 1' },
  { id: 'square', label: 'Perfect Square', value: 1, tip: '1 : 1' },
  { id: 'cinematic', label: 'Cinematic', value: 16 / 9, tip: '16 : 9' },
] as const

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b)
}

function AspectRatioSolver() {
  const [width, setWidth] = useState(512)
  const [height, setHeight] = useState(320)
  const [padding, setPadding] = useState(15)

  const data = useMemo(() => {
    const w = Math.max(1, width)
    const h = Math.max(1, height)
    const ratio = w / h
    const divisor = gcd(w, h)
    const simplified = `${w / divisor} : ${h / divisor}`

    let match: (typeof RATIOS)[number] | null = null
    for (const r of RATIOS) {
      if (Math.abs(ratio - r.value) < 0.02) {
        match = r
        break
      }
    }

    // Preview dimensions capped for display
    const maxBox = 260
    const scale = Math.min(maxBox / w, maxBox / h, 1)
    const boxW = Math.round(w * scale)
    const boxH = Math.round(h * scale)

    return { ratio, simplified, match, boxW, boxH }
  }, [width, height])

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Controls */}
      <div className="rounded-2xl border border-white/8 bg-[#0b1020] p-6 shadow-xl shadow-black/40">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="ar-w" className="mb-2 block text-sm font-medium text-slate-300">
              Width (px)
            </label>
            <input
              id="ar-w"
              type="number"
              min={1}
              value={width}
              onChange={(e) => setWidth(Number(e.target.value))}
              className="w-full rounded-xl border border-white/8 bg-[#070a13] px-3 py-2.5 font-mono text-sm text-white focus:border-indigo-400 focus:outline-none"
            />
          </div>
          <div>
            <label htmlFor="ar-h" className="mb-2 block text-sm font-medium text-slate-300">
              Height (px)
            </label>
            <input
              id="ar-h"
              type="number"
              min={1}
              value={height}
              onChange={(e) => setHeight(Number(e.target.value))}
              className="w-full rounded-xl border border-white/8 bg-[#070a13] px-3 py-2.5 font-mono text-sm text-white focus:border-indigo-400 focus:outline-none"
            />
          </div>
        </div>

        <div className="mt-6">
          <div className="mb-2 flex items-center justify-between">
            <label htmlFor="ar-pad" className="text-sm font-medium text-slate-300">
              Safe Padding
            </label>
            <span className="font-mono text-sm font-semibold text-indigo-300">{padding}%</span>
          </div>
          <input
            id="ar-pad"
            type="range"
            min={10}
            max={25}
            value={padding}
            onChange={(e) => setPadding(Number(e.target.value))}
            className="w-full"
            aria-valuemin={10}
            aria-valuemax={25}
            aria-valuenow={padding}
          />
        </div>

        <div className="mt-6 space-y-3 rounded-xl border border-white/8 bg-white/[0.02] p-4 font-mono text-sm">
          <div className="flex items-center justify-between">
            <span className="text-slate-400">Computed Ratio</span>
            <span className="text-white">{data.ratio.toFixed(3)} : 1</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400">Simplified</span>
            <span className="text-white">{data.simplified}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400">Standard Match</span>
            {data.match ? (
              <span className="rounded-md bg-emerald-500/15 px-2 py-0.5 text-emerald-400">
                {data.match.label} ({data.match.tip})
              </span>
            ) : (
              <span className="rounded-md bg-amber-500/15 px-2 py-0.5 text-amber-400">Custom</span>
            )}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          {RATIOS.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => {
                setWidth(320 * (r.value >= 1 ? r.value : 1))
                setHeight(320 * (r.value >= 1 ? 1 : 1 / r.value))
              }}
              className="rounded-xl border border-white/8 bg-white/[0.02] px-2 py-2 text-xs font-medium text-slate-300 transition-colors hover:border-indigo-400/50 hover:text-white"
            >
              {r.tip}
            </button>
          ))}
        </div>
      </div>

      {/* Live Preview */}
      <div className="flex items-center justify-center rounded-2xl border border-white/8 bg-[#0b1020] p-6 shadow-xl shadow-black/40">
        <div
          className="relative flex items-center justify-center rounded-xl border-2 border-dashed border-indigo-400/40 bg-indigo-500/5 transition-all duration-300"
          style={{ width: data.boxW, height: data.boxH }}
          aria-label="Logo wrapper preview"
        >
          <div
            className="flex items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500/80 to-emerald-400/80 shadow-lg shadow-indigo-500/30 transition-all duration-300"
            style={{
              width: `${100 - padding * 2}%`,
              height: `${100 - padding * 2}%`,
            }}
          >
            <span className="font-mono text-xs font-semibold text-[#070a13]">LOGO</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AspectRatioSolver
